import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CalendarClock, History } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const OP_ESTADOS = [
  { key: 'por_contactar', label: 'Por contactar', cls: 'bg-slate-100 text-slate-700' },
  { key: 'nao_atendeu', label: 'Não atendeu', cls: 'bg-rose-100 text-rose-700' },
  { key: 'aguarda_fatura', label: 'Aguarda fatura', cls: 'bg-sky-100 text-sky-700' },
  { key: 'agendamento_presencial', label: 'Agendamento presencial', cls: 'bg-violet-100 text-violet-700' },
  { key: 'sem_troca', label: 'Sem troca (mantém-se)', cls: 'bg-amber-100 text-amber-800' },
  { key: 'contrato_fechado', label: 'Contrato fechado', cls: 'bg-emerald-100 text-emerald-700' },
];

const fmtDT = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }); } catch { return iso; }
};

export const OpFollowCell = ({ item, onSaved }) => {
  const [pending, setPending] = useState(null);
  const [nota, setNota] = useState('');
  const [quando, setQuando] = useState('');
  const [saving, setSaving] = useState(false);
  const [showHist, setShowHist] = useState(false);
  const cur = OP_ESTADOS.find((e) => e.key === item.op_estado) || OP_ESTADOS[0];

  const start = (key) => {
    if (key === item.op_estado && key !== 'agendamento_presencial') return;
    setPending(key);
    setNota(key === item.op_estado ? item.op_nota || '' : '');
    setQuando(key === 'agendamento_presencial' && item.op_agendamento ? item.op_agendamento.slice(0, 16) : '');
  };

  const save = async () => {
    if (pending === 'agendamento_presencial' && !quando) { toast.error('Indique a data e hora'); return; }
    setSaving(true);
    try {
      await axios.put(`${API_URL}/oportunidades/${item.id}/estado`, {
        estado: pending, nota, agendamento: pending === 'agendamento_presencial' ? new Date(quando).toISOString() : null,
      });
      toast.success('Estado atualizado');
      setPending(null);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao guardar');
    } finally {
      setSaving(false);
    }
  };

  const pendingLabel = OP_ESTADOS.find((e) => e.key === pending)?.label;

  return (
    <div className="space-y-1" data-testid={`op-follow-${item.id}`}>
      <Select value={cur.key} onValueChange={start}>
        <SelectTrigger className={`h-7 text-[11px] font-semibold border-0 rounded-full px-2.5 w-auto gap-1 ${cur.cls}`} data-testid={`op-estado-select-${item.id}`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {OP_ESTADOS.map((e) => (
            <SelectItem key={e.key} value={e.key} data-testid={`op-estado-opt-${e.key}`}>{e.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      {item.op_estado === 'agendamento_presencial' && item.op_agendamento && (
        <p className="text-[11px] text-violet-700 flex items-center gap-1"><CalendarClock className="w-3 h-3" /> {fmtDT(item.op_agendamento)}</p>
      )}
      {item.op_revisao && item.op_estado !== 'sem_troca' && (
        <p className="text-[10px] font-semibold text-orange-700 bg-orange-100 rounded-full px-2 py-0.5 w-fit" data-testid={`op-revisao-badge-${item.id}`}>Revisão 2 meses · {item.op_sem_troca_count}x sem troca</p>
      )}
      {item.op_estado === 'sem_troca' && item.op_revisao_at && (
        <p className="text-[10px] text-amber-800 flex items-center gap-1" data-testid={`op-revisao-next-${item.id}`}>
          <CalendarClock className="w-3 h-3" /> Revisão a {new Date(item.op_revisao_at).toLocaleDateString('pt-PT')}{item.op_sem_troca_count > 0 ? ` · ${item.op_sem_troca_count}x sem troca` : ''}
        </p>
      )}
      {item.op_auto_closed && <p className="text-[10px] text-emerald-700">Automático: novo contrato com o mesmo NIF</p>}
      {item.op_nota && <p className="text-[11px] text-slate-500 italic max-w-[220px] truncate" title={item.op_nota}>"{item.op_nota}"</p>}
      {item.op_history?.length > 0 && (
        <button type="button" onClick={() => setShowHist(true)} className="text-[10px] text-slate-400 hover:text-slate-700 flex items-center gap-1" data-testid={`op-history-btn-${item.id}`}>
          <History className="w-3 h-3" /> {item.op_updated_by_name} · {fmtDT(item.op_updated_at)}
        </button>
      )}

      <Dialog open={!!pending} onOpenChange={(o) => !o && setPending(null)}>
        <DialogContent className="max-w-md" data-testid="op-estado-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>{item.nome_cliente}: {pendingLabel}</DialogTitle></DialogHeader>
          {pending === 'agendamento_presencial' && (
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Data e hora do agendamento</label>
              <input type="datetime-local" value={quando} onChange={(e) => setQuando(e.target.value)} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" data-testid="op-agendamento-input" />
              <p className="text-[11px] text-slate-400 mt-1">O comercial recebe uma notificação no CRM 30 min antes.</p>
            </div>
          )}
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Nota (opcional)</label>
            <textarea value={nota} onChange={(e) => setNota(e.target.value)} rows={3} maxLength={500} className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm" placeholder={pending === 'sem_troca' ? 'Ex.: já tem bom preço' : 'Ex.: ligar depois das 18h'} data-testid="op-nota-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPending(null)}>Cancelar</Button>
            <Button onClick={save} disabled={saving} className="bg-[#2D3E6F] hover:bg-[#1a2a4a]" data-testid="op-estado-save">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showHist} onOpenChange={setShowHist}>
        <DialogContent className="max-w-md" data-testid="op-history-dialog" aria-describedby={undefined}>
          <DialogHeader><DialogTitle>Histórico: {item.nome_cliente}</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {[...(item.op_history || [])].reverse().map((h, i) => (
              <div key={i} className="text-xs border-l-2 border-slate-200 pl-2">
                <p className="font-semibold text-slate-700">{h.label}{h.agendamento ? ` · ${fmtDT(h.agendamento)}` : ''}</p>
                <p className="text-slate-400">{h.by} · {fmtDT(h.at)}</p>
                {h.nota && <p className="text-slate-600 italic">"{h.nota}"</p>}
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
